import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { useOAuthReturn, isOAuthReturn } from '../hooks/useOAuthReturn';
import { useOrgTimezone, formatDateTimeInTz } from '../utils/timezone';
import myobService, { type MyobStatus, type MyobCompanyFile } from '../services/myob';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plug,
  Plug2,
  RefreshCw,
  Building2,
} from 'lucide-react';

/**
 * MYOB AccountRight integration — per-organisation OAuth + company file picker.
 *
 * Linked from HRManagement → "MYOB Integration".
 * Gated on `useModuleLicense('timesheet', 'hr')`.
 *
 * TODO: When per-org Xero Payroll is added, build a mirror of this page at
 * /accounts/xero — same OAuth + status pattern, but with extra
 * steps for selecting a payroll calendar and mapping each internal payroll
 * category to a Xero EarningsRateID/LeaveTypeID GUID.
 */
function MyobSettings() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  // The connection is the Accounts module's. Any accounts seat reads it; the
  // admin seat changes it (the server refuses otherwise).
  const { hasAccess: hasHr, loading: hrLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<MyobStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [companyFiles, setCompanyFiles] = useState<MyobCompanyFile[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const [cfUsername, setCfUsername] = useState('');
  const [cfPassword, setCfPassword] = useState('');
  const [savingFile, setSavingFile] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await myobService.getStatus();
      if (res.success && res.data) setStatus(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load MYOB status.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // MYOB sends the browser back with a code; the person signed in here
  // completes the sign-in (see useOAuthReturn).
  const completeSignIn = useCallback((code: string, state: string) => myobService.completeSignIn(code, state), []);
  const onSignedIn = useCallback(() => {
    setInfo('MYOB connected successfully. Now select your company file below.');
    void loadStatus();
  }, [loadStatus]);
  const finishingSignIn = useOAuthReturn(completeSignIn, onSignedIn, setError, 'MYOB was not connected. Please connect again from this page.');

  // Surface OAuth callback outcome from query params, then clean them up.
  useEffect(() => {
    if (isOAuthReturn(searchParams)) return;
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('error');
    if (connected === 'true') {
      setInfo('MYOB connected successfully. Now select your company file below.');
      searchParams.delete('connected');
      setSearchParams(searchParams, { replace: true });
      void loadStatus();
    } else if (oauthError) {
      const map: Record<string, string> = {
        missing_params: 'OAuth callback missing parameters.',
        invalid_state: 'OAuth state validation failed. Please try again.',
        connection_failed: 'MYOB connection failed. Please try again.',
      };
      setError(map[oauthError] || 'MYOB connection error.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadStatus]);

  const handleConnect = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await myobService.getAuthorizationUrl();
      if (res.success && res.data?.authorization_url) {
        // Same-tab redirect — MYOB returns to /api/myob/callback which redirects back here
        window.location.href = res.data.authorization_url;
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start OAuth flow.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect MYOB? Direct API uploads will stop working until you reconnect.')) return;
    setError(null);
    setBusy(true);
    try {
      await myobService.disconnect();
      setInfo('MYOB disconnected.');
      setCompanyFiles(null);
      setSelectedFileId('');
      setCfUsername('');
      setCfPassword('');
      await loadStatus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Disconnect failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleListFiles = async () => {
    setError(null);
    setFilesLoading(true);
    try {
      const res = await myobService.listCompanyFiles();
      if (res.success && res.data) {
        setCompanyFiles(res.data);
        if (res.data.length === 0) {
          setError('This MYOB sign-in has no company file that can be used here.');
        } else if (status?.company_file_id) {
          setSelectedFileId(status.company_file_id);
          setCfUsername(status.company_file_username || '');
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to list company files.');
    } finally {
      setFilesLoading(false);
    }
  };

  const handleSaveFile = async () => {
    setError(null);
    setInfo(null);
    if (!selectedFileId) { setError('Pick a company file.'); return; }
    if (!cfUsername || !cfPassword) {
      setError('Enter the MYOB sign-in username and password for the selected company file.');
      return;
    }
    const file = companyFiles?.find(f => f.id === selectedFileId);
    if (!file) { setError('Selected file not found.'); return; }
    setSavingFile(true);
    try {
      await myobService.setCompanyFile({
        id: file.id,
        uri: file.uri,
        name: file.name,
        username: cfUsername,
        password: cfPassword,
      });
      setInfo('Company file saved. You can now use "Direct API upload" from the Payroll Manager.');
      setCfPassword('');
      await loadStatus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save company file.');
    } finally {
      setSavingFile(false);
    }
  };

  if (hrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }
  if (!hasHr) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Plug className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">An Accounts licence seat is required to access this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  const connected = status?.connected ?? false;
  const fileSelected = status?.company_file_selected ?? false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PageHeader
        title="MYOB Integration"
        subtitle="Connect this organisation to MYOB AccountRight to upload payroll timesheets directly via the API"
        icon={<Plug className="h-6 w-6 text-white" />}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {!isAccountsAdmin && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 break-words">
            You can read this page. An Accounts Admin makes changes to it.
          </div>
        )}
        <fieldset disabled={!isAccountsAdmin} className="space-y-6 min-w-0 border-0 p-0 m-0">

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
            <XCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {info && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <span>{info}</span>
          </div>
        )}
        {finishingSignIn && (
          <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700 flex items-start gap-2">
            <RefreshCw className="h-5 w-5 shrink-0 mt-0.5 animate-spin" />
            <span className="min-w-0 break-words">Finishing the MYOB sign-in…</span>
          </div>
        )}

        {/* Step 1: Connection */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <Plug2 className="h-5 w-5 text-primary-600" />
                  Step 1 — MYOB connection
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  OAuth2 sign-in to MYOB. Tokens are stored encrypted and refreshed automatically.
                </p>
              </div>
              <div className="text-right">
                {statusLoading ? (
                  <span className="text-sm text-neutral-500">Loading…</span>
                ) : connected ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 text-xs font-medium">
                    Not connected
                  </span>
                )}
              </div>
            </div>

            {connected && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm [&>div]:min-w-0">
                <div>
                  <p className="text-xs text-neutral-500">MYOB user</p>
                  <p className="font-medium text-neutral-900 break-words">{status?.myob_user_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Connected at</p>
                  <p className="font-medium text-neutral-900 break-words">
                    {status?.connected_at ? formatDateTimeInTz(status.connected_at, orgTz) : '—'}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-5 flex gap-2">
              {!connected ? (
                <Button onClick={handleConnect} disabled={busy}>
                  {busy ? 'Redirecting…' : 'Connect MYOB'}
                </Button>
              ) : (
                <Button variant="outline" onClick={handleDisconnect} disabled={busy}>
                  Disconnect
                </Button>
              )}
              <Button variant="outline" onClick={() => void loadStatus()} disabled={statusLoading} className="flex items-center gap-1">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Company file */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary-600" />
                  Step 2 — Company file
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Pick the AccountRight company file to upload to and supply the file's sign-in credentials.
                </p>
              </div>
              {fileSelected ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Configured
                </span>
              ) : connected ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" /> Not configured
                </span>
              ) : null}
            </div>

            {fileSelected && (
              <div className="mb-5 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm">
                <p className="text-emerald-900">
                  Currently uploading to <span className="font-semibold">{status?.company_file_name}</span>
                  {status?.company_file_username ? (
                    <> as <span className="font-mono">{status.company_file_username}</span></>
                  ) : null}
                  .
                </p>
                <p className="text-emerald-900 mt-1 break-words">
                  To use a different company file, disconnect MYOB and connect again.
                </p>
              </div>
            )}

            {!connected ? (
              <p className="text-sm text-neutral-500">Complete Step 1 first.</p>
            ) : (
              <>
                {!companyFiles ? (
                  <Button onClick={handleListFiles} disabled={filesLoading}>
                    {filesLoading ? 'Loading…' : (fileSelected ? 'Update sign-in details' : 'List company files')}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">Company file</label>
                      <select
                        value={selectedFileId}
                        onChange={e => setSelectedFileId(e.target.value)}
                        // Chosen once: the server refuses a different file.
                        disabled={fileSelected}
                        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100 disabled:text-neutral-600"
                      >
                        <option value="">— Select a file —</option>
                        {companyFiles.map(f => (
                          <option key={f.id} value={f.id}>
                            {f.name}{f.product_level ? ` · ${f.product_level}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Company file username</label>
                        <input
                          type="text"
                          value={cfUsername}
                          onChange={e => setCfUsername(e.target.value)}
                          placeholder="Administrator"
                          autoComplete="off"
                          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Company file password</label>
                        <input
                          type="password"
                          value={cfPassword}
                          onChange={e => setCfPassword(e.target.value)}
                          autoComplete="new-password"
                          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500">
                      These are the credentials you use when opening the company file in AccountRight itself —
                      stored encrypted alongside the OAuth tokens.
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveFile} disabled={savingFile}>
                        {savingFile ? 'Saving…' : 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => setCompanyFiles(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Last export panel */}
        {status?.last_export_at && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Last API upload</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-neutral-500">When</p>
                  <p className="font-medium text-neutral-900 break-words">
                    {formatDateTimeInTz(status.last_export_at, orgTz)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Status</p>
                  <p className="font-medium text-neutral-900 capitalize">{status.last_export_status || '—'}</p>
                </div>
              </div>
              {status.last_export_error && (
                <pre className="mt-3 text-xs bg-red-50 border border-red-200 rounded p-3 whitespace-pre-wrap text-red-800">
                  {status.last_export_error}
                </pre>
              )}
            </CardContent>
          </Card>
        )}

        </fieldset>
      </main>
    </div>
  );
}

export default MyobSettings;
